const SUBSCRIPTION_STATUS_PRIORITY = ["active", "past_due", "incomplete"];
const TRIAL_DAYS = 10;

const getDocData = (doc) => {
  if (!doc) return null;
  if (typeof doc.data === "function") return doc.data();
  if (doc.data && typeof doc.data === "object") return doc.data;
  return doc;
};

const toDateMs = (value) => {
  if (!value) return null;

  if (typeof value.toDate === "function") {
    const converted = value.toDate();
    const ms = converted?.getTime?.();
    return Number.isFinite(ms) ? ms : null;
  }

  const parsedDate = value instanceof Date ? value : new Date(value);
  const ms = parsedDate.getTime();
  return Number.isFinite(ms) ? ms : null;
};

export const selectPreferredSubscriptionDoc = (subscriptionDocs = []) => {
  if (!Array.isArray(subscriptionDocs) || subscriptionDocs.length === 0) {
    return null;
  }

  for (const status of SUBSCRIPTION_STATUS_PRIORITY) {
    const match = subscriptionDocs.find(
      (doc) => getDocData(doc)?.status === status,
    );

    if (match) {
      return match;
    }
  }

  return [...subscriptionDocs].sort((a, b) => {
    const aMs = toDateMs(getDocData(a)?.current_period_end) ?? -Infinity;
    const bMs = toDateMs(getDocData(b)?.current_period_end) ?? -Infinity;
    return bMs - aMs;
  })[0];
};

export const deriveFallbackSubscriptionState = ({
  trialPeriodStartDate,
  trialOverrideEndAt,
  now = new Date(),
  trialDays = TRIAL_DAYS,
} = {}) => {
  const nowDate = now instanceof Date ? now : new Date(now);
  const trialOverrideEndMs = toDateMs(trialOverrideEndAt);

  if (
    Number.isFinite(nowDate.getTime()) &&
    Number.isFinite(trialOverrideEndMs) &&
    nowDate.getTime() <= trialOverrideEndMs
  ) {
    return {
      subscriptionStatus: "trialing",
      maxClients: 100,
      showingSubscriptionEnded: false,
    };
  }

  const trialStartDate =
    trialPeriodStartDate instanceof Date
      ? new Date(trialPeriodStartDate.getTime())
      : new Date(trialPeriodStartDate);

  if (
    !Number.isFinite(nowDate.getTime()) ||
    !Number.isFinite(trialStartDate.getTime())
  ) {
    return {
      subscriptionStatus: "canceled",
      maxClients: 0,
      showingSubscriptionEnded: true,
    };
  }

  trialStartDate.setDate(trialStartDate.getDate() + trialDays);
  const inTrialPeriod = nowDate <= trialStartDate;

  if (inTrialPeriod) {
    return {
      subscriptionStatus: "trialing",
      maxClients: 100,
      showingSubscriptionEnded: false,
    };
  }

  return {
    subscriptionStatus: "canceled",
    maxClients: 0,
    showingSubscriptionEnded: true,
  };
};

export const resolveEmptySubscriptionState = ({
  currentState,
  trialPeriodStartDate,
  trialOverrideEndAt,
  allowDowngradeFallback = true,
  isAuthoritative = false,
  now = new Date(),
} = {}) => {
  if (!allowDowngradeFallback || !isAuthoritative) {
    return {
      subscriptionStatus: currentState?.subscriptionStatus ?? "active",
      maxClients: currentState?.maxClients ?? 100,
      showingSubscriptionEnded: currentState?.showingSubscriptionEnded ?? false,
    };
  }

  return deriveFallbackSubscriptionState({
    trialPeriodStartDate,
    trialOverrideEndAt,
    now,
  });
};

export const shouldShowSubscriptionUpgrade = ({
  subscriptionStatus,
  isSubscriptionAuthoritative,
} = {}) => {
  return (
    isSubscriptionAuthoritative === true && subscriptionStatus !== "active"
  );
};

export const isSubscriptionSnapshotAuthoritative = (snapshot) => {
  return snapshot?.metadata?.fromCache === false;
};
